import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SortDescriptor } from '@progress/kendo-data-query'
import { FieldSettings } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridColumnProps,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { UserSession } from 'api/Identity/Types/UserSession'
import { PageData } from 'api/Pagination/Types'
import {
  isTicketPayments,
  TicketDto,
  TicketFilterKey,
  TicketPaymentsDto,
} from 'api/Tickets/Types/TicketDto'
import { TicketStatuses, TicketStatusesIds } from 'api/Tickets/Types/TicketStatus'
import { AxiosError, isAxiosError } from 'axios'
import { Box } from 'components/Box'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { getTicketFilterType } from 'components/Filter'
import { SelectAssigneeFilter } from 'components/Filter/ClientFields/SelectAssigneeFilter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Link } from 'components/Link'
import { Spinner } from 'components/Spinner'
import { errorToast, Toast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchAdministrationUsersAction,
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import {
  acquireClientTicketsAction,
  assignTicketsAction,
  completeClientTicketsAction,
  completeTicketsAction,
  getAndSetTicketsAssignedToUserSession,
  getMlStatusesAction,
  getTicketStatusTypeAction,
  getTicketTypeAction,
  processPaymentTicketsAction,
  unAssignTicketsAction,
  undoAcquireClientTicketsAction,
} from 'store/tickets/tickets.actions'
import { fetchTransactionStatusAction } from 'store/transactions/transaction.action'
import { getWalletPaymentProvidersActions } from 'store/wallet/wallet-actions'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { SelectClientEvent, SelectClientTable } from '../../Records/Clients/SelectClientTable'
import { isMyTicketView, TicketsViewType } from '../TicketsViewType'
import { normalizeTicketsSortQuery } from './normalizer'
import {
  SelectTicketEvent,
  SelectTicketsAssignTable,
  SelectTicketsEvent,
} from './SelectTicketsAssignTable'

import styles from './Tickets.module.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  statuses: useAppSelector((state) => state.tickets.statuses),
  mlStatuses: useAppSelector((state) => state.tickets.mlStatuses),
  transactionStatuses: useAppSelector((state) => state.transactions.status),
  types: useAppSelector((state) => state.tickets.types),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  paymentProviders: useAppSelector((state) => state.wallet.paymentProviders),
  users: useAppSelector((state) => state.identity.administrationUsers),

  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    completeTickets: async (
      ids: string[],
      viewTypeId: TicketsViewType,
      setToast: (toast: Toast) => void
    ) => dispatch(completeTicketsAction(ticketsApiClient, ids, viewTypeId, setToast)),
    completeClientTickets: async (
      user: UserSession,
      ids: string[],
      viewTypeId: TicketsViewType,
      setToast: (toast: Toast) => void
    ) => dispatch(completeClientTicketsAction(ticketsApiClient, user, ids, viewTypeId, setToast)),
    acquireClientTickets: async (
      user: UserSession,
      ids: string[],
      viewTypeId: TicketsViewType,
      setToast: (toast: Toast) => void
    ) => dispatch(acquireClientTicketsAction(ticketsApiClient, user, ids, viewTypeId, setToast)),
    assignClientTickets: async (
      assigneeId: string,
      ids: string[],
      onFilterFetch: () => void,
      setToast: (toast: Toast) => void
    ) => dispatch(assignTicketsAction(ticketsApiClient, assigneeId, ids, onFilterFetch, setToast)),
    undoAcquireClientTickets: async (
      ticketIds: string[],
      viewTypeId: TicketsViewType,
      setToast: (toast: Toast) => void
    ) =>
      dispatch(undoAcquireClientTicketsAction(ticketsApiClient, ticketIds, viewTypeId, setToast)),
    unAssignClientTickets: async (
      ticketIds: string[],
      onFilterFetch: () => void,
      setToast: (toast: Toast) => void
    ) => dispatch(unAssignTicketsAction(ticketsApiClient, ticketIds, onFilterFetch, setToast)),
    processTransactionsTickets: async (
      transactionIds: string[],
      onFilterFetch: () => void,
      setToast: (toast: Toast) => void
    ) =>
      dispatch(
        processPaymentTicketsAction(ticketsApiClient, transactionIds, onFilterFetch, setToast)
      ),
    fetchTicketTypes: async (viewType: number) =>
      dispatch(getTicketTypeAction(viewType, ticketsApiClient)),
    fetchTicketStatuses: async () => dispatch(getTicketStatusTypeAction(ticketsApiClient)),
    fetchMlStatuses: async () => dispatch(getMlStatusesAction(ticketsApiClient)),
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchUsers: async () => dispatch(fetchAdministrationUsersAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    getTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    getWalletPaymentProvider: async () =>
      dispatch(getWalletPaymentProvidersActions(walletsApiClient)),
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    setTicketsAssignedToUser: async () =>
      dispatch(getAndSetTicketsAssignedToUserSession(ticketsApiClient)),
  }
}

const getSelectedIds = (tickets: TicketDto[]) => {
  return tickets
    .filter((ticket) => ticket.selected)
    .filter((ticket) => ticket.status.id !== TicketStatusesIds.CLOSED)
    .map((ticket) => ticket.id)
}

const getTransactionIds = (tickets: TicketDto[]) => {
  return tickets
    .filter((ticket) => ticket.selected)
    .filter((ticket) => Object.keys(ticket.data).includes('transaction'))
    .filter((ticket) => ticket.status.id !== TicketStatusesIds.CLOSED)
    .map((ticket) => (ticket.data as TicketPaymentsDto).transaction.id)
}

const getTransactionReferences = (tickets: TicketDto[]) => {
  return tickets
    .filter((ticket) => ticket.selected)
    .filter((ticket) => isTicketPayments(ticket))
    .filter((ticket) => ticket.status.id !== TicketStatusesIds.CLOSED)
    .map((ticket) => {
      const transaction = (ticket.data as TicketPaymentsDto).transaction
      return {
        id: transaction.id,
        reference: transaction.name,
      }
    })
}

const getSelectedTicketsNotAssignedToCurrentUser = (tickets: TicketDto[]) => {
  return tickets
    .filter((ticket) => ticket.selected)
    .filter((t) => !t.assignee)
    .filter((ticket) => ticket.status.id !== TicketStatusesIds.CLOSED)
    .map((ticket) => ticket.id)
}

interface Props {
  filterFields: () => FieldSettings[]
  columns: GridColumn[]
  viewType: TicketsViewType
  myTicketsType?: number
}

const Tickets: React.FC<Props> = ({ columns, viewType, filterFields, myTicketsType }) => {
  const [tickets, setTickets] = useState<TicketDto[]>([])
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const ticketsApiClient = useApiClient(TicketsApiClient)

  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isAssignUsersModalOpen, setAssignUsersModalOpen] = useState<boolean>(false)
  const [isSelectAssignUsersModalOpen, setSelectAssignUsersModalOpen] = useState<boolean>(false)
  const [isNegativeBalanceCheckModalOpen, setNegativeBalanceCheckModalOpen] =
    useState<boolean>(false)
  const [isNegativeBalanceCheckReferences, setNegativeBalanceCheckReferences] = useState<
    { reference: string; id: string }[]
  >([])

  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [assignModalFilterId, setAssignModalFilterId] = useState<string>()

  const setToast = useContext(ToastContext)
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const permissions = auth?.permissions || []
  const {
    filters,
    statuses,
    mlStatuses,
    transactionStatuses,
    users,
    tickmillCompanies,
    paymentProviders,
    gridFilterByFilterIdSelector,
  } = useMapStateToProps()
  const {
    undoAcquireClientTickets,
    unAssignClientTickets,
    processTransactionsTickets,
    completeTickets,
    completeClientTickets,
    assignClientTickets,
    acquireClientTickets,
    fetchTicketTypes,
    fetchTicketStatuses,
    fetchMlStatuses,
    fetchUsers,
    getTickmillCompanies,
    getWalletPaymentProvider,
    fetchFilters,
    fetchTransactionStatus,
    setTicketsAssignedToUser,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<TicketDto[]> | undefined> => {
          try {
            const searchQuery = getQuery(gridFilterState.filter)
            const sortQuery = getSort(gridFilterState.sort)
            const response = await ticketsApiClient.getTickets(viewType, {
              search: searchQuery,
              sort: sortQuery,
              pageIndex: gridFilterState.pageIndex,
              pageSize: gridFilterState.pageSize,
              skipCount: tableData?.skipCount !== false,
              myTicketsViewTypeId: myTicketsType,
            })
            await fetchTicketTypes(viewType)

            if (isEmpty(transactionStatuses)) await fetchTransactionStatus()
            if (isEmpty(statuses)) await fetchTicketStatuses()
            if (viewType === TicketsViewType.Payments && isEmpty(mlStatuses)) {
              await fetchMlStatuses()
            }
            if (isEmpty(tickmillCompanies)) await getTickmillCompanies()
            if (isEmpty(paymentProviders)) await getWalletPaymentProvider()
            if (isEmpty(filters)) await fetchFilters()
            if (isEmpty(users)) await fetchUsers()

            if (response?.items) {
              setTickets(
                ((response || {}).items || []).map((x) => Object.assign({ selected: false }, x))
              )
            }
            setTableData({ ...tableData, meta: response })

            return response
          } catch (error) {
            console.error(error, 'error')
          }
        }
      )
    })()
  }, [myTicketsType]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TicketDto[]> | undefined>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response && response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTicketsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await ticketsApiClient.getTickets(viewType, {
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
        myTicketsViewTypeId: myTicketsType,
      })
      const total = response.totalResults
      if (response?.items) {
        setTickets(response.items.map((x) => Object.assign({ selected: false }, x)))
      }

      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      viewType,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await ticketsApiClient.getTickets(viewType, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
      myTicketsViewTypeId: myTicketsType,
    })
    const total = response.totalResults

    if (response?.items) {
      setTickets(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }
  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await ticketsApiClient.getTickets(viewType, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
      myTicketsViewTypeId: myTicketsType,
    })

    if (response?.items) {
      setTickets(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const onPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await ticketsApiClient.getTickets(viewType, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
      myTicketsViewTypeId: myTicketsType,
    })
    const total = response.totalResults

    if (response?.items) {
      setTickets(response.items.map((x) => Object.assign({ selected: false }, x)))
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await ticketsApiClient.getTickets(viewType, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
      myTicketsViewTypeId: myTicketsType,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTickets(response.items.map((x) => Object.assign({ selected: false }, x)))
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleSelectAssignUsersModalOpen = (event: SelectClientModalOpenEvent) => {
    setSelectAssignUsersModalOpen(true)
    setAssignModalFilterId(event.filterId)
  }

  const handleSelectAssignUsersModalClose = () => {
    setSelectAssignUsersModalOpen(false)
    setAssignModalFilterId(undefined)
  }

  const handleAssignUserModalOpen = () => {
    setAssignUsersModalOpen(true)
  }

  const handleAssignUsersModalClose = () => {
    setAssignUsersModalOpen(false)
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedUsersAssign = (event: SelectTicketsEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
      setSelectAssignUsersModalOpen(false)
    }
  }

  const handleUserAssign = async (event: SelectTicketEvent) => {
    const userId = event.value.id
    await assignClientTickets(userId, getSelectedIds(tickets), filterFetch, setToast)
    setAssignUsersModalOpen(false)
  }

  const handleUnAssign = async () => {
    await unAssignClientTickets(getSelectedIds(tickets), filterFetch, setToast)
  }

  const handleProcessTransactions = async () => {
    try {
      await processTransactionsTickets(getTransactionIds(tickets), filterFetch, setToast)
    } catch (error: unknown) {
      const err = error as AxiosError
      if (isAxiosError(err.response)) {
        if (
          err.response.response?.data.code === 'not_enough_trading_account_equity' ||
          err.response.response?.data.code === 'not_enough_trading_account_equities'
        ) {
          const references = getTransactionReferences(tickets)
          setNegativeBalanceCheckReferences(references)
          setNegativeBalanceCheckModalOpen(true)
        }
      }
    }
  }

  const handleGridSelectionChange = (event: GridSelectionChangeEvent) => {
    setTickets(
      tickets.map((item) => {
        if (item.id === event.dataItem.id) {
          item.selected = !event.dataItem.selected
        }
        return item
      })
    )
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setTickets(
      tickets.map((item) => {
        return {
          ...item,
          selected: isChecked,
        }
      })
    )
  }

  const handleCompleteTickets = async () => {
    await completeTickets(getSelectedIds(tickets), viewType, setToast)
    await onPageChange(gridState.pageIndex)
  }

  const handleCompleteClientTickets = async () => {
    if (auth) await completeClientTickets(auth.user, getSelectedIds(tickets), viewType, setToast)
    await onPageChange(gridState.pageIndex)
  }

  const handleAcquireTickets = async () => {
    if (auth) {
      const ids = getSelectedTicketsNotAssignedToCurrentUser(tickets)
      if (isEmpty(ids)) {
        return setToast(
          errorToast(
            'Cannot acquire these tickets. Please select Tickets not Closed and not assigned to you.'
          )
        )
      }
      await ticketsApiClient.acquireTickets({
        ticketIds: ids,
        viewTypeId: viewType,
        assigneeId: auth?.user.id,
      })
      setToast({ type: 'success', title: `${ids.length} Tickets acquired successfully.` })
      setTicketsAssignedToUser()
      await onPageChange(gridState.pageIndex)
    }
  }

  const handleAcquireClientTickets = async () => {
    if (auth) await acquireClientTickets(auth.user, getSelectedIds(tickets), viewType, setToast)
    setTicketsAssignedToUser()
    await onPageChange(gridState.pageIndex)
  }

  const handleUndoAcquireTickets = async () => {
    const ids = getSelectedIds(tickets)
    if (isEmpty(ids)) {
      return setToast(
        errorToast(
          `Tickets are empty. Remember, cannot acquire ticket with status: ${TicketStatuses.CLOSED}.`
        )
      )
    }
    await ticketsApiClient.acquireTickets({ ticketIds: ids, viewTypeId: viewType })
    setToast({ type: 'success', title: 'Tickets returned successfully.' })
    setTicketsAssignedToUser()
    await onPageChange(gridState.pageIndex)
  }

  const handleUndoAcquireClientTickets = async () => {
    const selectedIds = getSelectedIds(tickets)
    await undoAcquireClientTickets(selectedIds, viewType, setToast)
    setTicketsAssignedToUser()
    await onPageChange(gridState.pageIndex)
  }

  const getFilters = (): FieldSettings[] => {
    const clientFilter = {
      name: TicketFilterKey.CLIENT,
      label: 'Client',
      filter: (clientFilterProps: {
        filter: FilterDescriptor
        onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
      }) => <SelectClientFilter {...clientFilterProps} onClientModalOpen={handleClientModalOpen} />,
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    }

    const assigneeFilter = {
      name: TicketFilterKey.ASSIGNEE,
      label: 'Assignee',
      filter: (assigneeFilterProps: {
        filter: FilterDescriptor
        onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
      }) => (
        <SelectAssigneeFilter
          {...assigneeFilterProps}
          onAssignModalOpen={handleSelectAssignUsersModalOpen}
        />
      ),
      operators: [
        {
          text: GridFilterOperatorNameEnum.equalOperator,
          operator: GridFilterOperatorEnum.equalOperator,
        },
        {
          text: GridFilterOperatorNameEnum.notEqOperator,
          operator: GridFilterOperatorEnum.notEqOperator,
        },
      ],
    }

    return isMyTicketView(viewType)
      ? [clientFilter, ...filterFields()]
      : [clientFilter, ...filterFields(), assigneeFilter]
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isAssignUsersModalOpen && (
        <Dialog
          open={isAssignUsersModalOpen}
          onOpenChange={(open) => !open && handleAssignUsersModalClose()}
        >
          <SelectTicketsAssignTable
            departments={tickets.length ? tickets[0].departments : []}
            onSingleSelect={handleUserAssign}
            selectText='Assign'
          />
        </Dialog>
      )}
      {isSelectAssignUsersModalOpen && (
        <Dialog
          open={isSelectAssignUsersModalOpen}
          onOpenChange={(open) => !open && handleSelectAssignUsersModalClose()}
        >
          <SelectTicketsAssignTable
            departments={tickets.length ? tickets[0].departments : []}
            filterId={assignModalFilterId}
            onMultipleSelect={handleSelectedUsersAssign}
            selectText='Select'
          />
        </Dialog>
      )}
      {isNegativeBalanceCheckModalOpen && (
        <Modal
          visible={isNegativeBalanceCheckModalOpen}
          onClose={() => setNegativeBalanceCheckModalOpen(false)}
          width={550}
        >
          <div className={styles.borderBottom}>Negative Equity Check</div>
          <div className={styles.textContainer}>
            Please be informed that the following transactions among your selected list are not
            processed due to negative equity check failure:
            <ol>
              {isNegativeBalanceCheckReferences.map(({ id, reference }, index) => (
                <li key={index}>
                  <Link
                    to={{
                      pathname: generatePath('/transactions/:id', {
                        id: id,
                      }),
                    }}
                  >
                    {reference}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </Modal>
      )}

      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={getTicketFilterType(viewType)}
        filterFields={getFilters()}
        getQuery={getQuery}
        extraHeaderActions={
          !isMyTicketView(viewType) && (
            <Box display='flex' flexDirection='row' style={{ marginBottom: 24 }}>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      disabled={!hasPermission(permissions, PermissionsEnum.CanCompleteTickets)}
                      type='button'
                      variant='outline'
                      size='sm'
                    />
                  }
                >
                  Acquire <ChevronDown />
                </DropdownMenuTrigger>

                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onSelect={handleAcquireTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAcquireTickets)}
                  >
                    Acquire Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleAcquireClientTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAcquireClientTickets)}
                  >
                    Acquire Client Tickets
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleUndoAcquireTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAcquireTickets)}
                  >
                    Undo Acquire Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleUndoAcquireClientTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAcquireClientTickets)}
                  >
                    Undo Acquire Client tickets
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      disabled={!hasPermission(permissions, PermissionsEnum.CanCompleteTickets)}
                      type='button'
                      variant='outline'
                      size='sm'
                    />
                  }
                >
                  Complete <ChevronDown />
                </DropdownMenuTrigger>

                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onSelect={handleCompleteTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanCompleteTickets)}
                  >
                    Complete Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleCompleteClientTickets}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanCompleteClientTickets)}
                  >
                    Complete Client tickets
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      disabled={!hasPermission(permissions, PermissionsEnum.CanAssignTickets)}
                      type='button'
                      variant='outline'
                      size='sm'
                    />
                  }
                >
                  Actions <ChevronDown />
                </DropdownMenuTrigger>

                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onSelect={handleAssignUserModalOpen}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAssignTickets)}
                  >
                    Assign
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={handleUnAssign}
                    disabled={!hasPermission(permissions, PermissionsEnum.CanAssignTickets)}
                  >
                    Unassign
                  </DropdownMenuItem>
                  {viewType === TicketsViewType.Payments && (
                    <DropdownMenuItem
                      onSelect={handleProcessTransactions}
                      disabled={
                        !tickets.filter((ticket) => ticket.selected).length ||
                        !!tickets
                          .filter((ticket) => ticket.selected && ticket.data)
                          .filter(
                            (ticket) =>
                              !(ticket.data as TicketPaymentsDto).transaction?.canBulkProcess
                          ).length
                      }
                    >
                      Process Transactions
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </Box>
          )
        }
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={tickets} columns={columns}>
          <Grid
            {...gridState}
            resizable
            pageable
            selectedField='selected'
            sortable={{
              allowUnsort: true,
              mode: 'multiple',
            }}
            data={tickets}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            onSelectionChange={handleGridSelectionChange}
            onHeaderSelectionChange={handleHeaderSelectionChange}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={onPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            <Column
              field='selected'
              width='50px'
              headerSelectionValue={tickets.findIndex((dataItem) => !dataItem.selected) === -1}
            />
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default Tickets
